import { takeLatest, put, select, call } from "redux-saga/effects";
import { v4 as uuidv4 } from 'uuid';
import flow from 'lodash/fp/flow';
import map from 'lodash/fp/map';
import omit from 'lodash/fp/omit';
import compact from 'lodash/compact';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import _first from 'lodash/first';
import _some from 'lodash/some';
import _includes from 'lodash/includes';
import dynamicGalleryApi from '../../api/dynamicGalleryApi';
import galleryApi from '../../api/galleryApi';
import config from '../../config';
import { GET_GALLERY_WITH_FALLBACKS } from "../../actions/actionTypes";
import safeSaga from "../safeSaga";
import { skuMapSelector } from '../../selectors/skuMap/skuMapSelector';
import {
  configuratorSelector,
  productDetailsSelector,
  gallerySelector,
  productSelector,
  productInfoSelector,
} from "../../selectors";
import gallerySearchTokenGenerator from "../../utils/gallerySearchTokenGenerator";
import { updateGallery, parseHarmonyGallery } from "../../actions/getGallery";

function* isFullyVisualized(primaryImage, skuMap) {
  // fully visualized images are named using skus
  // eg. {sku}__{sku}-x{quantity}
  let allSkus;

  if (isEmpty(skuMap.subSkus)) {
    allSkus = [skuMap.sku];
  } else {
    allSkus = map(s => s.sku)(skuMap.subSkus)
  }

  const fullyVisualizedImageMatch = _some(allSkus, (id) => _includes(primaryImage, id));
  return fullyVisualizedImageMatch;
}

function* generateGallerySearchToken(skuMap, product, choices, productId, options, visualizedSiteProductOptions) {
  const token = gallerySearchTokenGenerator(
    skuMap,
    product,
    choices,
    productId,
    options,
    visualizedSiteProductOptions
  );

  return token;
}

function* getItemIds(productInfo) {
  const attachmentItemIds = flow(
    omit(['DefaultProduct', 'primary']),
    map(p => p.itemId),
  )(productInfo) || [];

  const itemIds = compact([get(productInfo, 'primary.itemId'), ...attachmentItemIds.slice(0, 4)]);
  return itemIds;
}

function* getDynamicGalleryImage(productInfo, galleryAssets) {
  const itemIds = yield call(getItemIds, productInfo);

  if (!isEmpty(itemIds)) {
    try {
      // check if calling dynamic gallery returns a valid image
      const { status } = yield call(dynamicGalleryApi, itemIds);
      if (status === 200) {
        // If image[0].url does not change media gallery component will not re-render
        // So pass it a unique identifier to force thumbnails to re-render
        const forceReRender = uuidv4();
        galleryAssets.unshift({
          id: 'dynamicGalleryImage',
          sizes: ["100", "145", "600", "1000"],
          type: 'IMAGE',
          url: `${config.dynamicGalleryApiUrl}?${itemIds.map(x => `items=${encodeURIComponent(x)}`).join('&')}&hei=<SIZE>&${forceReRender}`,
          primaryLink: `${config.dynamicGalleryApiUrl}?${itemIds.map(x => `items=${encodeURIComponent(x)}`).join('&')}&hei=<SIZE>`,
          thumbnail: `${config.dynamicGalleryApiUrl}?${itemIds.map(x => `items=${encodeURIComponent(x)}`).join('&')}&hei=<SIZE>`,
          source: "THD_DYNAMIC_GALLERY",
          });
      }
    } catch (err) {
      console.log(err);
    }
  }

  yield put(updateGallery({ assets: galleryAssets }));
}

function* getGallery() {
  let galleryAssetData;

  try {
    const product = yield select(productSelector);
    const skuMap = yield select(skuMapSelector);
    const { choices, options } = yield select(configuratorSelector);
    const {
      visualizedSiteProductOptions,
      productId,
      attributes: {
        dynamicGallery,
      },
    } = yield select(productDetailsSelector);

    if (typeof (productId) === 'undefined') return;

    const searchToken = yield call(generateGallerySearchToken, skuMap, product, choices, productId, options, visualizedSiteProductOptions);

    const folder = product?.details?.attributes?.scene7Folder;

    if (!folder) return;

    const gallery = yield call(galleryApi, folder, searchToken);
    const galleryData = gallery.assets.map(x => {
      const id = x.s3ObjectName;
      const url = x.primaryLink.replace("hei=1000", "hei=<SIZE>") || x.primaryLink.replace("hei=100", "hei=<SIZE>");
      const videoStill = x.mediaType === 'VIDEO' ? x.mainImage : null;
      const sizes = ["100", "145", "600", "1000"];

      return {
        id,
        sizes,
        type: x.mediaType,
        url,
        source: "CONFIGURABLE_S3",
        videoStill,
        primaryLink: x.primaryLink,
        thumbnail: x.thumbnail,
      };
    });

    galleryAssetData = galleryData;

    const productInfo = yield select(productInfoSelector);

    if (dynamicGallery === 'Yes') {
      yield call(getDynamicGalleryImage, productInfo, galleryAssetData);
    } else {
      if (galleryData.length) {
        const hasFullVisualization = yield call(
          isFullyVisualized,
          searchToken,
          skuMap,
        );

        if (hasFullVisualization) {
          yield put(updateGallery({assets: galleryAssetData}));
        } else {
          yield call(getDynamicGalleryImage, productInfo, galleryAssetData);
        }
      }
    }
  } catch (err) {
    console.log(err);
  } finally {
    const { assets } = yield select(gallerySelector);
    if (assets.length > 0) {
      yield put(parseHarmonyGallery(assets));
    }
  }
}

export default function* gallerySaga() {
  yield takeLatest(GET_GALLERY_WITH_FALLBACKS, safeSaga(getGallery));
}
